import { useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash } from '@phosphor-icons/react'
import { isApiError } from '@/api'
import { Banner, Button, Field, IconButton, Input, IntegerInput, Select } from '@/ui'
import { errorDetail, errorMessage } from '../../api/core'
import { useOrgs } from '../../api/reference'
import { useLocations } from '../../api/store'
import { useCreateDispatch, useReleasedPackLots } from '../../api/recall'
import { FormDialog } from '../FormDialog'
import {
  DISPATCH_ERROR_SENTENCE,
  dispatchProblems,
  emptyDispatchForm,
  hasDispatchErrors,
  normalizePackLotCode,
  problemSentence,
  toDispatchBody,
  validateDispatchForm,
  type DispatchFormValues,
  type DispatchProblem,
  type LotLeft,
} from '../../model/recall/dispatchForm'
import { obj, str } from '../../api/mappers'

/** New dispatch (F7.8): customer, invoice, vehicle, clean bed, and pack lots with pouches. */
export function NewDispatchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const listId = useId()
  const keySeq = useRef(1)
  const [f, setF] = useState<DispatchFormValues>(emptyDispatchForm)
  const [touched, setTouched] = useState(false)
  const [refused, setRefused] = useState<{ message: string; problems: DispatchProblem[] } | null>(null)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setF(emptyDispatchForm())
      setTouched(false)
      setRefused(null)
    }
  }

  const halls = useLocations({ kind: 'PACKING', level: 'SITE', page: 1, limit: 100 }, open)
  const buyers = useOrgs('BUYER')
  const lots = useReleasedPackLots(open)
  const released = useMemo(() => {
    const m = new Map<string, LotLeft & { skuName: string | null }>()
    for (const l of lots.data?.rows ?? []) m.set(l.code.toUpperCase(), { code: l.code, pouchesLeft: l.pouchesLeft, skuName: l.skuName })
    return m
  }, [lots.data])
  const complete = Boolean(lots.data && lots.data.total <= lots.data.rows.length)
  const errors = validateDispatchForm(f, released, complete)
  const show = touched ? errors : { items: {} }

  const create = useCreateDispatch((e) => {
    if (isApiError(e) && e.status === 422) {
      const detail = errorDetail(e)
      const code = e.code ?? str(detail.errorCode)
      setRefused({
        message: DISPATCH_ERROR_SENTENCE[code] ?? errorMessage(e),
        problems: dispatchProblems(obj(detail).errors),
      })
      return true
    }
  })

  const set = (patch: Partial<DispatchFormValues>) => {
    setF((p) => ({ ...p, ...patch }))
    setRefused(null)
  }
  const setItem = (key: string, patch: Partial<DispatchFormValues['items'][number]>) =>
    set({ items: f.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) })
  const addItem = () => set({ items: [...f.items, { key: `i${++keySeq.current}`, code: '', pouches: null }] })
  const removeItem = (key: string) => set({ items: f.items.filter((i) => i.key !== key) })

  const totalPouches = f.items.reduce((s, i) => s + (i.pouches ?? 0), 0)

  const save = async () => {
    setTouched(true)
    if (hasDispatchErrors(errors)) throw new Error('invalid')
    const res = await create.mutateAsync(toDispatchBody(f))
    const id = str(obj(res).id)
    if (id) navigate(`/c/dispatches/${id}`)
  }

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="New dispatch"
      description="Record the trip before the vehicle leaves. Only released pack lots that are not on hold can go."
      saveLabel="Dispatch"
      loading={create.isPending}
      onSave={save}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="From (packing hall)" required error={show.fromLocationId}>
          <Select
            value={f.fromLocationId}
            onChange={(fromLocationId) => set({ fromLocationId })}
            placeholder={halls.isLoading ? 'Loading…' : 'Choose…'}
            options={(halls.data?.rows ?? []).map((h) => ({ value: h.id, label: h.name || h.code }))}
          />
        </Field>
        <Field label="Customer" required error={show.toOrgId} hint="Registered buyers only.">
          <Select
            value={f.toOrgId}
            onChange={(toOrgId) => set({ toOrgId })}
            placeholder={buyers.isLoading ? 'Loading…' : 'Choose…'}
            options={(buyers.data ?? []).filter((o) => o.kind === 'BUYER').map((o) => ({ value: o.id, label: o.name }))}
          />
        </Field>
        <Field label="Invoice or challan number" required error={show.challanNo}>
          <Input value={f.challanNo} onChange={(e) => set({ challanNo: e.target.value })} maxLength={30} placeholder="e.g. INV-114" />
        </Field>
        <Field label="Vehicle number" required error={show.vehicleNo}>
          <Input value={f.vehicleNo} onChange={(e) => set({ vehicleNo: e.target.value })} maxLength={20} placeholder="e.g. RJ27GA1234" className="font-mono uppercase" />
        </Field>
        <Field label="Driver name" error={show.driverName}>
          <Input value={f.driverName} onChange={(e) => set({ driverName: e.target.value })} maxLength={80} />
        </Field>
        <Field label="Driver phone" error={show.driverPhone}>
          <Input value={f.driverPhone} onChange={(e) => set({ driverPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })} inputMode="numeric" className="font-mono" />
        </Field>
      </div>

      <div className="flex flex-col gap-1">
        <label className="flex items-start gap-2 text-[14.5px]">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-amber"
            checked={f.vehicleClean}
            onChange={(e) => set({ vehicleClean: e.target.checked })}
          />
          <span>Vehicle bed empty and clean, no chemicals</span>
        </label>
        {show.vehicleClean && <p className="text-[13px] font-medium text-danger">{show.vehicleClean}</p>}
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">Pack lots on the vehicle</legend>
        <datalist id={listId}>
          {(lots.data?.rows ?? []).map((l) => (
            <option key={l.id} value={l.code}>
              {`${l.skuName ?? ''} · ${l.pouchesLeft} left`}
            </option>
          ))}
        </datalist>
        {f.items.map((it, idx) => {
          const lot = released.get(normalizePackLotCode(it.code))
          const err = show.items[it.key]
          return (
            <div key={it.key} className="grid grid-cols-[minmax(0,1fr)_7rem_auto] items-start gap-2">
              <Field label={idx === 0 ? 'Pack lot (scan a carton or pick)' : <span className="sr-only">Pack lot</span>} error={err?.code}
                hint={lot ? `${lot.skuName ?? 'Pouches'} · ${lot.pouchesLeft} left at the hall` : undefined}
              >
                <Input
                  list={listId}
                  value={it.code}
                  onChange={(e) => setItem(it.key, { code: e.target.value })}
                  placeholder="PL-…"
                  className="font-mono"
                  autoComplete="off"
                />
              </Field>
              <Field label={idx === 0 ? 'Pouches' : <span className="sr-only">Pouches</span>} error={err?.pouches}>
                <IntegerInput value={it.pouches} onChange={(pouches) => setItem(it.key, { pouches })} />
              </Field>
              <IconButton
                label="Remove this row"
                variant="ghost"
                className={idx === 0 ? 'mt-7' : undefined}
                onClick={() => removeItem(it.key)}
                disabled={f.items.length === 1}
                icon={<Trash aria-hidden="true" />}
              />
            </div>
          )
        })}
        {touched && errors.itemsForm && <p className="text-[13px] font-medium text-danger">{errors.itemsForm}</p>}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button type="button" size="sm" variant="secondary" leadingIcon={<Plus />} onClick={addItem} disabled={f.items.length >= 100}>
            Add lot
          </Button>
          <span className="font-mono text-[13px] text-muted">Total {totalPouches} pouches</span>
        </div>
        {lots.isError && <p className="text-[13px] text-muted">Could not load the released lots; type the codes and the server will check them.</p>}
      </fieldset>

      {refused && (
        <Banner tone="error" title={refused.message}>
          {refused.problems.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-ink">
              {refused.problems.map((p, i) => (
                <li key={i}>{problemSentence(p)}</li>
              ))}
            </ul>
          )}
        </Banner>
      )}
    </FormDialog>
  )
}
